import apiService from './api';

/** One autocomplete suggestion. */
export interface PlaceSuggestion {
  place_id: string;
  text: string;
  main: string;
  secondary: string;
}

/** A picked address, split the way our forms want it (HHNodeServer utils/placesAddress.js). */
export interface PlaceAddress {
  line1: string | null;
  line2: string | null;
  suburb: string | null;
  state: string | null;
  /** The council area ("Shire of Campaspe") — names a growing region made from a farm's address. */
  area?: string | null;
  postcode: string | null;
  country: string | null;
  /** "AU". */
  country_code?: string | null;
  plus_code: string | null;
  /** false = the address does not pin a door (a rural road, a named property); a plus code earns its place. */
  has_street_number: boolean;
  formatted: string | null;
  lat: number | null;
  lng: number | null;
  /** [south, north, west, east] — the same order the Nominatim search returns. */
  bbox: [number, number, number, number] | null;
}

let enabled: Promise<boolean> | null = null;

/**
 * Whether address lookup is set up on the server. Asked once per page load:
 * every address field on a page shares the answer, and a missing key is not
 * going to appear mid-session.
 */
export function placesEnabled(): Promise<boolean> {
  if (!enabled) {
    enabled = apiService
      .get<{ enabled: boolean }>('/places/config')
      .then((res) => Boolean(res.success && res.data?.enabled))
      .catch(() => false);
  }
  return enabled;
}

/** Forget the cached answer. Tests only — a page load asks once. */
export function resetPlacesEnabled(): void {
  enabled = null;
}

/**
 * A token for one address being typed. Google bills the keystrokes and the
 * pick that ends them as a single session only when they share it.
 */
export function newPlacesSession(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

export async function autocompletePlaces(q: string, session: string): Promise<PlaceSuggestion[]> {
  const res = await apiService.get<PlaceSuggestion[]>(
    `/places/autocomplete?q=${encodeURIComponent(q)}&session=${encodeURIComponent(session)}`,
  );
  return res.success && res.data ? res.data : [];
}

export async function placeDetails(placeId: string, session: string): Promise<PlaceAddress | null> {
  const res = await apiService.get<PlaceAddress>(
    `/places/details/${encodeURIComponent(placeId)}?session=${encodeURIComponent(session)}`,
  );
  return res.success && res.data ? res.data : null;
}
